import React, { useState, Fragment, useEffect, useMemo, useCallback } from 'react';

import { getTutorials } from '../../../store/tutorials';
import { getCoverData, initialFVICoverData } from '../../../store/coverData';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import { DEFAULT_REPORT_TITLE, TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';
import { AnomalySummary, Column, CustomUTMOverview, NewAnomalyItem, PDFImageProps, PIDData, SelectOption, UTMInspection, User } from '../../../utils/models';
import { getCurrentUser } from '../../../services/authService';
import { getPipingAnomalyClassOptions, getUTMPipingInspectionOptions, getUTMStructureInspectionOptions } from '../../../utils/data';
import { matchPhrases, reArrangeAnomalies, reArrangePhotoRecord, validateWorkOrderNumber } from '../../../utils/utilities';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomUTMInspectionOverviewForm from '../../forms/custom-utm/customUTMInspectionOverviewForm';
import CustomUTMSummaryReadingsForm from '../../forms/custom-utm/customUTMSummaryReadingsForm';
import CustomNewItem from '../custom-general/custom-new-item';
import CustomUTMInspectionFormNewItem from '../../forms/custom-utm/customUTMInspectionFormNewItem';
import CustomUTMInspectionItem from '../custom-utm/custom-utm-inspection-item';
import Elevation from '../../themed/instruction-modals/elevation';
import ExecutiveSummaryForm from '../../forms/visual-inspection/executiveSummaryForm';
import LoadingBadge from '../../themed/loading-badge';
import Modal from '../../themed/modal';
import FVICoverData from '../../themed/fvi-cover-data';
import FVIStructurePIDImageForm from '../../forms/custom-fvi/fviStructurePIDImageForm';
import MUTIsoPageForm from '../../forms/mut/mutIsoPageForm';
import MUTPhotograpicRecord from '../../forms/mut/mutPhotographicRecord';
import MultiSelect from '../../themed/multiselect';
import NewPipingAnomalyItems from '../custom-visual-inspection/piping-anomalies/new-piping-anomalies';
import PDFTablePage from '../../themed/pdf/pdfTablePage';
import PipingAnomalySummary from '../../forms/custom-fvi/pipingAnomalySummary';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import SyncTime from '../../blocks/sync-time';
import WarningNotice from '../custom-visual-inspection/warning-notice';
import TextArea from '../../common/textarea';
import TextEditor from '../../themed/text-editor';
import Zippy from '../../themed/zippy';

import CustomExxonMobilRiserReport, { RiserChecklist, RiserChecklistItem, UTMData } from '.';

import useAutoSaveReport from '../../../hooks/useAutoSave';
import useInspectedItemIDUpdate from '../../../hooks/useInspectedItemIDUpdate';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';
import useProjectDetails from '../../../hooks/useProjectDetails';

const CreateCustomExxonMobilRiserReport: React.FC = () => {
	const { fviCoverData } = useAppSelector(getCoverData);
	const { isSaving } = useAppSelector(getReportDetails);

	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [coverData, setCoverData] = useState(initialFVICoverData);
	const [executiveSummary, setExecutiveSummary] = useState('');
	const [pidData, setPIDData] = useState<PIDData>({ hasPID: 'yes', pIDNumber: '', pIDImages: [] });
	const [isoImage, setIsoImage] = useState<PDFImageProps[]>([]);
	const [anomalySummary, setAnomalySummary] = useState<AnomalySummary>(initialAnomalySummary);
	const [anomalyItems, setAnomalyItems] = useState<NewAnomalyItem[]>([]);
	const [photographicData, setPhotographicData] = useState<PDFImageProps[]>([]);
	const [checklistData, setChecklistData] = useState<RiserChecklist>({ checklist: initialChecklistItems, comment: '' });
	const [utmData, setUTMData] = useState<UTMData>({
		overview: initialUTMOverview,
		inspectionItems: [],
		thicknessReadings: [],
	});

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { role } = getCurrentUser() as User;

	const reportDetails = useMemo(() => {
		return {
			reportTitle,
			coverData,
			executiveSummary,
			checklistData,
			anomalyItems,
			pidData,
			photographicData,
			anomalySummary,
			projectDetails,
			isoImage,
			utmData,
		};
	}, [anomalyItems, anomalySummary, projectDetails, checklistData, coverData, executiveSummary, isoImage, pidData, photographicData, reportTitle, utmData]);

	const mappedAnomalies = useMemo(() => {
		const anomalies = anomalyItems.map(anomaly => ({ id: anomaly.id.toString(), name: anomaly.id.toString() }));
		return [{ id: 'N/A', name: 'N/A'}].concat(anomalies);
	}, [anomalyItems]);

	const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();

	const handleChecklistAnomalySelect = useCallback((item: RiserChecklistItem, anomalies: SelectOption[]) => {
		const updatedChecklistItems = checklistData.checklist.map((checklistItem) => checklistItem.component === item.component ? ({ ...item, anomalyNumber: anomalies }) : checklistItem);
		setChecklistData({ ...checklistData, checklist: updatedChecklistItems });
	}, [checklistData]);

	const handleChecklistAssessmentEdit = useCallback((item: RiserChecklistItem, assessmentResult: string ) => {
		const updatedChecklistItems = checklistData.checklist.map((checklistItem) => checklistItem.component === item.component ? ({ ...item, assessmentResult }) : checklistItem);
		setChecklistData({ ...checklistData, checklist: updatedChecklistItems});
	}, [checklistData]);

	const columns = useMemo(() => {
		return [
			{
				path: 'component',
				label: 'COMPONENT',
				headerStyle: { backgroundColor: '#ccccff' },
				style: () => ({ backgroundColor: '#dfe1e6', color: '#000000', textTransform: 'uppercase', textAlign: 'left' }),
			},
			{
				path: 'assessmentResult',
				label: 'ASSESSMENT RESULT',
				headerStyle: { backgroundColor: '#34cccd' },
				content: (item: RiserChecklistItem) => (
					<>
						<TextArea 
							value={item.assessmentResult} 
							onChange={(event) => handleChecklistAssessmentEdit(item, event.currentTarget.value)}
						/>
					</>
				),
			},
			{
				path: 'anomalyNumber',
				label: 'ANOMALY NUMBER',
				headerStyle: { backgroundColor: '#ccccff' },
				content: (item: RiserChecklistItem) => {
					return (
						<>
							<MultiSelect
								onSearch={(value) => {}}
								loading={false}
								options={mappedAnomalies}
								singleSelect={false}
								placeholder="Select Anomaly"
								label=""
								onSelect={(items) => handleChecklistAnomalySelect(item, items)}
								onRemove={(items) => handleChecklistAnomalySelect(item, items)}
								selectedValues={item.anomalyNumber}
								showCheckbox
								error={item.anomalyNumber.length === 0 ? 'Select an anomaly' : undefined}
							/>
						</>
					)
				},
			},
		]
	}, [mappedAnomalies, handleChecklistAnomalySelect, handleChecklistAssessmentEdit]);

	const utmDataInvalid = useMemo(() => {
		return false;
	}, []);

	const checklistInvalid = useMemo(() => {
		return checklistData.checklist.some((item) => item.assessmentResult.length === 0 || item.anomalyNumber.length === 0);
	}, [checklistData]);

	const validationMessage = useMemo(() => {
		if (!validateWorkOrderNumber(coverData.workOrderNumber))
			return 'Enter a valid work order number!';

		if (!executiveSummary) {
			return 'Executive summary missing!';
		}

		if (matchPhrases(pidData.hasPID, 'yes') && pidData.pIDImages.length === 0) {
			return 'P&ID images missing!';
		}

		if (checklistInvalid) {
			return 'At least one checklist item missing assessment / photo!';
		}

		const summaryMissing = Object.keys(anomalySummary).some((key) => {
			return ['', undefined].includes(anomalySummary[key as keyof typeof anomalySummary] as string)
		});

		if (summaryMissing) {
			return 'Anomaly Summary Incomplete!';
		}

		if (isoImage.length === 0) {
			return 'Please upload Isometric drawing!';
		}
		
		if (utmDataInvalid) {
			return 'Please complete the UTM section!';
		}

		return '';
	}, [anomalySummary, checklistInvalid, coverData, executiveSummary, pidData, isoImage, utmDataInvalid])

	const inspectedItems = useMemo(() => {
		return anomalyItems.map((anomalyItem) => anomalyItem.location);
	}, [anomalyItems]);

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}
		
		setPreviewReport((p: boolean) => !p);
	}, [validationMessage]);

	const handleAddInspectionItem = (newItem: UTMInspection) => {
		const inspectionItemMatch = utmData.inspectionItems.find((item) => matchPhrases(item.sectionTitle, newItem.sectionTitle));
		if (inspectionItemMatch) return toastService.error(`Section ${inspectionItemMatch.sectionTitle} already exists!`);

		setUTMData(previous => ({ ...previous, inspectionItems: [...previous.inspectionItems, newItem] }));
	};

	const handleEditInspectionItem = useCallback((originalItem: UTMInspection, updatedItem: UTMInspection) => {
		const updatedItems = utmData.inspectionItems.map((item) => item.sectionTitle === originalItem.sectionTitle ? updatedItem : item);
		setUTMData(previous => ({ ...previous, inspectionItems: updatedItems }));
	}, [utmData.inspectionItems]);

	const handleDeleteInspectionItem = useCallback((itemToDelete: UTMInspection) => {
		const items = utmData.inspectionItems.filter((item) => item.sectionTitle !== itemToDelete.sectionTitle);
		setUTMData((previous) => ({ ...previous, inspectionItems: items }));
	}, [utmData.inspectionItems]);

	const handleSaveAndPreview = useCallback(() => {
		autoSave.saveReport();
		togglePreviewReport();
	}, [autoSave, togglePreviewReport]);
	
	useInspectedItemIDUpdate(inspectedItems);
	
	useEffect(() => {
		function initializeCache() {
			const cachedReport = autoSave.oldReportData as typeof reportDetails | null;
			if (!cachedReport) {
				if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);
				
				return;
			}
			
			setAnomalySummary(cachedReport.anomalySummary);
			setReportTitle(cachedReport.reportTitle);
			setCoverData(cachedReport.coverData?.coverData?.length > 0 ? cachedReport.coverData : fviCoverData);
			setExecutiveSummary(cachedReport.executiveSummary);
			setPIDData(cachedReport.pidData);
			setChecklistData(cachedReport.checklistData);
			setIsoImage(cachedReport.isoImage);
			setAnomalyItems(reArrangeAnomalies(cachedReport.anomalyItems));
			setUTMData(cachedReport.utmData)
			setPhotographicData(reArrangePhotoRecord(cachedReport.photographicData));

			dispatch(setLastPhotoNumber(cachedReport.photographicData.length));
		}

		initializeCache();
	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.UTM));
	}, [dispatch]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<CustomExxonMobilRiserReport {...reportDetails} />
			</Modal>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			<FVICoverData
				coverDataId={config.coverDataTemplates.exxonMobilRiser}
				isVisible={projectDetails?.inspectionTypeId === config.supportedTemplates.exxonMobilRiser && role.approveReports}
				subtitle='Update the Cover Data information. This is the default data used on all ExxonMobil Riser Reports'
			/>

			<Conditional isVisible={validationMessage.length > 0}>
				<WarningNotice title="Report Incomplete" body={validationMessage} />
			</Conditional>

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm projectTitle={projectDetails?.projectTitle} onAdd={setReportTitle} />
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm initialData={coverData} onAddCoverForm={setCoverData} />
			</Zippy>
			
			<Zippy headerTitle="Executive Summary" isValid={executiveSummary.length > 0}>
				<ExecutiveSummaryForm onProceed={setExecutiveSummary} initialSummary={executiveSummary} />
			</Zippy>

			<Zippy headerTitle="P & ID" isValid={matchPhrases(pidData.hasPID, 'yes') ? pidData.pIDImages.length > 0 : true}>
				<FVIStructurePIDImageForm initialPIDData={pidData} onAdd={setPIDData} />
			</Zippy>

			<Zippy headerTitle="Anomaly Summary & Description" isValid={!checklistInvalid}>
				<PipingAnomalySummary
					hasAnomalies={anomalyItems.length > 0 ? 'yes' : 'no'}
					initialAnomalySummary={anomalySummary} 
					onAdd={(summary: AnomalySummary) => setAnomalySummary(summary)}/>
			</Zippy>

			<Zippy headerTitle="Checklist" isValid={!checklistInvalid}>
				<PDFTablePage data={checklistData.checklist} columns={columns as Column[]} />

				<div className="my-4 divider"></div>

				<TextEditor
					name='additional-comment'
					label=''
					value={checklistData.comment}
					placeholder='Enter Additional Comment'
					onChange={(comment) => setChecklistData({ ...checklistData, comment })} 
				/>
			</Zippy>

			<Zippy headerTitle="Iso Drawing" isValid={isoImage.length > 0}>
				<MUTIsoPageForm initialImages={isoImage} onProceed={setIsoImage} />
			</Zippy>

			<Zippy headerTitle="Anomaly Items" isValid={anomalyItems.length > 0}>
				<NewPipingAnomalyItems
					nextAnomalyID={anomalyItems.length + 1}
					damageClasses={getPipingAnomalyClassOptions()}
					anomalyItems={anomalyItems}
					onDataChange={(items: NewAnomalyItem[]) => setAnomalyItems(items)}
				/>
			</Zippy>

			<Zippy headerTitle="UTM Data (Optional)" isValid={!utmDataInvalid}>
				<Zippy headerTitle='UTM Overview'>
					<CustomUTMInspectionOverviewForm 
						initialData={utmData.overview} 
						onProceed={(overview: UTMData['overview']) => setUTMData((previous) => ({ ...previous, overview }))} 
					/>
				</Zippy>

				<Zippy headerTitle="Thickness Measurement Readings" isValid={utmData.thicknessReadings.length > 0}>
					<CustomUTMSummaryReadingsForm 
						options={getUTMPipingInspectionOptions()} 
						initialReadings={utmData.thicknessReadings} 
						onSave={(thicknessReadings: UTMData['thicknessReadings']) => setUTMData(previous => ({ ...previous, thicknessReadings }))} 
					/>
				</Zippy>

				<CustomNewItem label='Add New UTM Item'>
					<CustomUTMInspectionFormNewItem onAdd={handleAddInspectionItem} />
				</CustomNewItem>

				{utmData.inspectionItems.map((item, index) => (
					<CustomUTMInspectionItem
						onDeleteInspectionItem={handleDeleteInspectionItem}
						isOpenOnDefault={index === (utmData.inspectionItems.length - 1)}
						key={item.sectionTitle}
						includeSize={false}
						options={getUTMStructureInspectionOptions()}
						corrosionAllowance={utmData.overview.corrosionAllowance} 
						initialData={item} 
						onEditInspectionItem={(updatedItem) => handleEditInspectionItem(item, updatedItem)}
					/>
				))}
			</Zippy>

			<Zippy headerTitle="Photographic Report" isValid={photographicData.length > 0}>
				<MUTPhotograpicRecord initialImages={photographicData} onProceed={setPhotographicData} />
			</Zippy>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

const initialUTMOverview: CustomUTMOverview = {
	ndeTechnique: '',
	equipment: '',
	probe: '',
	amplitudeLinearityCheck: '',
	screenHeightLinearityCheck: '',
	calibrationBlk: '',
	refStd: '',
	serialNo: '',
	scanningLevel: '',
	couplant: '',
	remarks: '',
	material: '',
	calibrationDueDate: new Date(),
	dateOfUTM: null,
	corrosionAllowance: 0,
	unit: 'mm',
	summary: '',
	techniqueType: '',
	velocity: 0,
	data: [],
};

const initialChecklistItems: RiserChecklistItem[] = [
	{
		component: 'Riser Body',
		assessmentResult: '',
		anomalyNumber: []
	},
	{
		component: 'Riser Guard',
		assessmentResult: '',
		anomalyNumber: [],
	},
	{
		component: 'Riser Wrapping In Splash Zone',
		assessmentResult: '',
		anomalyNumber: [],
	},
	{
		component: '2nd Lower Clamp',
		assessmentResult: '',
		anomalyNumber: [],
	},
	{
		component: '1st Lower Clamp',
		assessmentResult: '',
		anomalyNumber: []
	},
	{
		component: 'Riser 1st Flange Joint',
		assessmentResult: '',
		anomalyNumber: [],
	}, 
	{
		component: 'Riser Wrapping Above 1st Flange',
		assessmentResult: '',
		anomalyNumber: [],
	},
	{
		component: 'Middle Clamp',
		assessmentResult: '',
		anomalyNumber: [],
	},
	{
		component: 'Upper Clamp',
		assessmentResult: '',
		anomalyNumber: [],
	},
	{
		component: 'Riser Grating Penetration',
		assessmentResult: '',
		anomalyNumber: [],
	},
	{
		component: 'MOV/SDV & Flanges',
		assessmentResult: '',
		anomalyNumber: [],
	},
	{
		component: 'Riser Paint Coating',
		assessmentResult: '',
		anomalyNumber: [],
	}
];

const initialAnomalySummary: AnomalySummary = {
	pipeBody: '',
	noOfPoints: 0,
	pitting: '',
	noOfPittingLocations: 0,
	flanges: '',
	noOfFlanges: 0,
	fasteners: '',
	valves: '',
	noOfValves: 0,
	corrosionUnderPipeSupport: '',
	noOfCups: 0,
	compositeWrapOnLine: 'no',
	noOfWrapPoints: 0,
	conditionOfSmallBorePipings: '',
	noOfSmallBorePipings: 0,
	others: '',
	description: '',
	extentOfCoatingBreakdown: '',
};

export default CreateCustomExxonMobilRiserReport;
